import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiEye, FiEyeOff } from 'react-icons/fi';

const DeleteAccountModal = ({ isOpen, onClose, onConfirm, isLocalAccount }) => {
    const [password, setPassword] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen && !loading) {
                setPassword('');
                setConfirmText('');
                onClose();
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, loading, onClose]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (confirmText !== 'SUPPRIMER') {
            return;
        }

        if (isLocalAccount && !password) {
            return;
        }

        setLoading(true);
        try {
            await onConfirm(password);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setPassword('');
            setConfirmText('');
            onClose();
        }
    };

    const isValid = confirmText === 'SUPPRIMER' && (!isLocalAccount || password.length >= 8);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-[1000]"
            onClick={(e) => {
                if (e.target === e.currentTarget && !loading) {
                    handleClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                                <FiAlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                            </div>
                            <h2 className="text-xl font-bold text-black dark:text-white">
                                Supprimer mon compte
                            </h2>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
                                    ⚠️ Attention : Cette action est irréversible !
                                </p>
                                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                                    <li>Votre compte sera définitivement supprimé</li>
                                    <li>Les organisations dont vous êtes propriétaire seront supprimées</li>
                                    <li>Tous vos messages et données seront perdus</li>
                                    <li>Vous serez retiré des organisations dont vous êtes membre</li>
                                </ul>
                            </div>

                            {isLocalAccount && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Confirmez votre mot de passe
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            required
                                            disabled={loading}
                                            minLength={8}
                                            placeholder="Votre mot de passe"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(s => !s)}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                            disabled={loading}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pour confirmer, tapez <span className="font-bold text-red-600 dark:text-red-400">SUPPRIMER</span>
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                                    className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    required
                                    disabled={loading}
                                    placeholder="SUPPRIMER"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex justify-end gap-3 border-t dark:border-gray-700">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition disabled:opacity-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={!isValid || loading}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Suppression...' : 'Supprimer définitivement'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DeleteAccountModal;
